/**
 * Shabbat entrance and exit times for Ashkelon.
 *
 * Sunset / tzeit use the NOAA solar algorithm (Jean Meeus / USNO),
 * the same engine Hebcal and KosherJava use.
 *
 * - כניסת שבת: 20 minutes before Friday sunset (Israeli minhag for Ashkelon;
 *   not Jerusalem's 40 or Haifa's 30). Floored to the minute so the clock
 *   is never later than the calculated instant.
 * - צאת שבת: tzeit hakochavim at 8.5° solar depression on Saturday night,
 *   rounded to the nearest minute (Hebcal / common Israeli calendar practice).
 */
import { HDate, Location, ParshaEvent, Zmanim, getSedra } from '@hebcal/core';

import { WEATHER_CITIES } from '@/lib/weather-cities';

const ASHKELON = WEATHER_CITIES.find((city) => city.key === 'ashkelon')!;

/** Israeli minhag outside Jerusalem / Haifa / Zikhron Ya'akov. */
export const ASHKELON_CANDLE_LIGHTING_MINUTES = 20;

/** Degrees below horizon for 3 small stars (Hebcal default). */
export const ASHKELON_TZEIT_DEGREES = 8.5;

const ASHKELON_LOCATION = new Location(
    ASHKELON.latitude,
    ASHKELON.longitude,
    true,
    'Asia/Jerusalem',
    'אשקלון',
    'IL',
);

const WEEKDAY_TO_INDEX: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};

/** Serializable payload for the homepage block (safe across SSR). */
export type ShabbatTimesInfo = {
    /** Gregorian Friday of this Shabbat (Asia/Jerusalem calendar day). */
    fridayKey: string;
    /** Gregorian Saturday of this Shabbat. */
    saturdayKey: string;
    /** Candle lighting HH:MM in Asia/Jerusalem. */
    entranceLabel: string;
    /** Havdalah / tzeit HH:MM in Asia/Jerusalem. */
    exitLabel: string;
    /** Parashat hashavua title without nikud, when available. */
    parashaLabel: string | null;
};

function jerusalemParts(now: Date) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
    }).formatToParts(now);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? '';

    const year = Number(get('year'));
    const month = Number(get('month'));
    const day = Number(get('day'));
    const weekday = WEEKDAY_TO_INDEX[get('weekday')] ?? -1;

    return { year, month, day, weekday };
}

/** Thursday (4), Friday (5), or Saturday (6) in Asia/Jerusalem. */
export function isIsraeliWeekend(now = new Date()): boolean {
    const { weekday } = jerusalemParts(now);
    return weekday >= 4 && weekday <= 6;
}

function fridayOfCurrentWeekend(now: Date): {
    year: number;
    month: number;
    day: number;
} {
    const { year, month, day, weekday } = jerusalemParts(now);
    const noon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const delta =
        weekday === 4 ? 1 : weekday === 5 ? 0 : weekday === 6 ? -1 : 0;
    noon.setUTCDate(noon.getUTCDate() + delta);
    return {
        year: noon.getUTCFullYear(),
        month: noon.getUTCMonth() + 1,
        day: noon.getUTCDate(),
    };
}

function formatTimeLabel(instant: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Jerusalem',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        hourCycle: 'h23',
    }).format(instant);
}

function dayKey(parts: { year: number; month: number; day: number }): string {
    const mm = String(parts.month).padStart(2, '0');
    const dd = String(parts.day).padStart(2, '0');
    return `${parts.year}-${mm}-${dd}`;
}

function addOneDay(parts: {
    year: number;
    month: number;
    day: number;
}): { year: number; month: number; day: number } {
    const noon = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    noon.setUTCDate(noon.getUTCDate() + 1);
    return {
        year: noon.getUTCFullYear(),
        month: noon.getUTCMonth() + 1,
        day: noon.getUTCDate(),
    };
}

function parashaForShabbat(friday: Date): string | null {
    const saturday = new Date(friday);
    saturday.setDate(saturday.getDate() + 1);
    const sedra = getSedra(new HDate(saturday).getFullYear(), true).lookup(
        new HDate(saturday),
    );
    if (sedra.chag) return null;
    const title = new ParshaEvent(sedra).render('he-x-nonikud');
    return title.replace(/\u05BE/g, '-');
}

/**
 * Candle lighting and havdalah for the Shabbat of the current Israeli weekend.
 * Returns null outside Thursday-Saturday (Asia/Jerusalem).
 */
export function getAshkelonShabbatTimes(
    now = new Date(),
): ShabbatTimesInfo | null {
    if (!isIsraeliWeekend(now)) return null;

    const fri = fridayOfCurrentWeekend(now);
    const sat = addOneDay(fri);
    const fridayDate = new Date(fri.year, fri.month - 1, fri.day, 12, 0, 0);
    const saturdayDate = new Date(sat.year, sat.month - 1, sat.day, 12, 0, 0);

    const fridayZmanim = new Zmanim(ASHKELON_LOCATION, fridayDate, false);
    const saturdayZmanim = new Zmanim(ASHKELON_LOCATION, saturdayDate, false);

    const candleLighting = fridayZmanim.sunsetOffset(
        -ASHKELON_CANDLE_LIGHTING_MINUTES,
        true,
    );
    const tzeit = saturdayZmanim.tzeit(ASHKELON_TZEIT_DEGREES);
    if (
        !candleLighting ||
        Number.isNaN(candleLighting.getTime()) ||
        !tzeit ||
        Number.isNaN(tzeit.getTime())
    ) {
        return null;
    }

    const havdalah = Zmanim.roundTime(tzeit);

    return {
        fridayKey: dayKey(fri),
        saturdayKey: dayKey(sat),
        entranceLabel: formatTimeLabel(candleLighting),
        exitLabel: formatTimeLabel(havdalah),
        parashaLabel: parashaForShabbat(fridayDate),
    };
}
